<script>
  import { format } from 'svelte-i18n';
  import { Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { Select } from '@client/components/select';
  import { Switch } from '@client/components/switch';

  import { sanitizeTagValue } from '@client/utils/sanitize';

  export let route = {};
  export let types = [];
  export let groups = [];
  export let campaigns = [];
  export let tags = [];

  let filterText = '';

  const onTagChange = (e) => {
    const currentTags = route?.tags || [];

    filterText = sanitizeTagValue(filterText);

    if (!route.tags) {
      route.tags = [];
      return;
    }

    if (e.key === 'Enter' || e.type === 'blur') {
      if (route?.tags?.map(({ value }) => value).includes(filterText)) {
        filterText = '';
        return;
      }

      if (!currentTags.includes(filterText) && filterText.length) {
        route.tags = [{ label: filterText, value: filterText }, ...(route.tags || [])];
        filterText = '';
      }
    } else {
      if (!filterText.length && e.detail.value) {
        const selected = e.detail.value;

        if (!currentTags.includes(selected) && selected.length) {
          route.tags = [selected, ...route.tags];
        }
      }
    }
  };

  $: sanitizedFilter = sanitizeTagValue(filterText || '');
  $: tagOptions =
    sanitizedFilter.length &&
    !tags.some(({ value }) => value === sanitizedFilter) &&
    !(route.tags || []).some(({ value }) => value === sanitizedFilter)
      ? [{ label: sanitizedFilter, value: sanitizedFilter, created: true }, ...tags]
      : tags;
</script>

<div class="form">
  <div class="section" aria-labelledby="type-label">
    <p id="type-label">
      {$format('label.TYPE')}
      <Icon name="help-circle" id="tooltip-group-targeting-type" />
      <Tooltip target="tooltip-group-targeting-type" placement="top">
        {@html $format('label.TOOLTIP.GROUP_TARGETING_TYPES')}
      </Tooltip>
    </p>
    <Select bind:items={types} bind:value={route.target} clearable={false} showChevron />
  </div>
  <div class="section" aria-labelledby="groups-label">
    <p id="groups-label">
      {$format('label.GROUPS')}
      <Icon name="help-circle" id="tooltip-group-targeting-groups" />
      <Tooltip target="tooltip-group-targeting-groups" placement="top">
        <b>{$format('label.GROUPS')}</b>
        <div>{$format('label.TOOLTIP.GROUP_TARGETING_GROUPS')}</div>
      </Tooltip>
    </p>
    <Select
      bind:items={groups}
      bind:value={route.groups}
      placeholder={$format('label.PLACEHOLDER.SELECT_GROUP')}
      multiple
      searchable
    />
  </div>
  <div class="section" aria-labelledby="campaigns-label">
    <p id="campaigns-label">
      {$format('label.CAMPAIGNS')}
      <Icon name="help-circle" id="tooltip-group-targeting-campaigns" />
      <Tooltip target="tooltip-group-targeting-campaigns" placement="top">
        <b>{$format('label.CAMPAIGNS')}</b>
        <div>{$format('label.TOOLTIP.GROUP_TARGETING_CAMPAIGNS')}</div>
      </Tooltip>
    </p>
    <Select
      bind:items={campaigns}
      bind:value={route.campaigns}
      placeholder={$format('label.PLACEHOLDER.SELECT_CAMPAIGNS')}
      multiple
      searchable
    />
  </div>
  <div class="section" aria-labelledby="tags-label" on:keyup={onTagChange} role="button" tabindex="0">
    <p id="tags-label">
      {$format('label.TAGS')}
      <Icon name="help-circle" id="tooltip-group-targeting-tags" />
      <Tooltip target="tooltip-group-targeting-tags" placement="top">
        <b>{$format('label.TAGS')}</b>
        <div>{$format('label.TOOLTIP.GROUP_TARGETING_TAGS')}</div>
      </Tooltip>
    </p>
    <Select
      items={tagOptions}
      itemFilter={(label, filterText, option) =>
        option?.created || `${label}`.toLowerCase().includes(filterText.toLowerCase())}
      bind:value={route.tags}
      bind:filterText
      on:change={onTagChange}
      on:blur={onTagChange}
      placeholder={$format('label.PLACEHOLDER.SELECT_TAGS')}
      multiple
      searchable
    >
      <svelte:fragment slot="item" let:item>
        {#if item.created}
          {@html $format('label.PRESS_ENTER_TO_CREATE_TAG', { values: { item: item.label } })}
        {:else}
          {item.label}
        {/if}
      </svelte:fragment>
      <slot name="empty" slot="empty">
        <div class="empty">
          {#if !filterText.length}
            {$format('label.START_TYPING_TO_FIND_TAGS')}
          {:else if route.tags?.map(({ value }) => value).includes(filterText)}
            {$format('label.TAG_ALREADY_EXISTS')}
          {:else}
            {@html $format('label.PRESS_ENTER_TO_CREATE_TAG', { values: { item: filterText } })}
          {/if}
        </div>
      </slot>
    </Select>
  </div>
  <div class="section" aria-labelledby="status-label">
    <p id="status-label">
      {$format('label.ACTIVE')}
      <Icon name="help-circle" id="tooltip-group-targeting-status" />
      <Tooltip target="tooltip-group-targeting-status" placement="top">
        <b>{$format('label.ACTIVE')}</b>
        <div>{$format('label.TOOLTIP.GROUP_TARGETING_STATUS')}</div>
      </Tooltip>
    </p>
    <Switch bind:checked={route.status} aria-labelledby="status-label" />
  </div>
</div>

<style lang="scss" src="./group-form-content.scss"></style>
